import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Loader2, Sparkles } from "lucide-react";
import { StepModel, StepColor, StepUpgrade, StepQty, fmt, preloadImages } from "@/components/swing_doors/SwingSteps";
import { StepContact, QuoteReveal } from "@/components/swing_doors/SwingContactAndQuote";
import { getAttribution } from "@/utils/attribution";

const BACKEND_URL = window.APP_CONFIG?.BACKEND_URL || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FUNNEL = "v5_swing_doors";
const STEPS = ["model", "color", "upgrade", "qty", "contact"];

const utm = () => {
  const p = new URLSearchParams(window.location.search);
  return Object.fromEntries(["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "gclid", "fbclid"].map((k) => [k, p.get(k)]));
};

export default function SwingingDoorQuote() {
  const [catalog, setCatalog] = useState(null);
  const [step, setStep] = useState(0);
  const [modelKey, setModelKey] = useState(null);
  const [color, setColor] = useState("white");
  const [upgradeKey, setUpgradeKey] = useState(null);
  const [qty, setQty] = useState(1);
  const [contact, setContact] = useState({ name: "", phone: "", email: "", address: "", zip: "" });
  const [submitting, setSubmitting] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [serverQuote, setServerQuote] = useState(null);
  const submissionId = useRef(null);
  const [sessionId] = useState(() => `sw_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);

  const track = (step_name, extra = {}) =>
    axios.post(`${API}/funnel/track`, { session_id: sessionId, step: STEPS.indexOf(step_name), step_name, event_type: step_name, funnel_version: FUNNEL, ...extra }).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/swing-doors/catalog`).then((r) => { setCatalog(r.data); preloadImages(r.data.upgrades.map((u) => u.image_url)); }).catch(() => toast.error("Couldn't load door styles. Please refresh."));
    const u = utm();
    track("visit", { utm_source: u.utm_source, utm_medium: u.utm_medium, utm_campaign: u.utm_campaign, metadata: { referrer: document.referrer || null, landing_page: window.location.pathname } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const model = catalog?.models.find((m) => m.key === modelKey);
  const upgrade = catalog?.upgrades.find((u) => u.key === upgradeKey);
  const colorLabel = catalog?.colors.find((c) => c.id === color)?.label;

  const quote = useMemo(() => {
    if (!model) return { items: [], subtotal: 0, tax: 0, total: 0, qty };
    const items = [{ name: `${model.name} (${colorLabel})`, quantity: qty, line_total: model.price * qty, taxable: model.is_taxed }];
    if (upgrade) items.push({ name: upgrade.name.replace(" (Swinging Door)", ""), quantity: qty, line_total: upgrade.price * qty, taxable: upgrade.is_taxed });
    const subtotal = items.reduce((s, i) => s + i.line_total, 0);
    const tax = Math.round(items.filter((i) => i.taxable).reduce((s, i) => s + i.line_total, 0) * catalog.tax_rate * 100) / 100;
    return { items, subtotal, tax, total: subtotal + tax, qty };
  }, [model, upgrade, colorLabel, qty, catalog]);

  const setters = Object.fromEntries(Object.keys(contact).map((k) => [k, (v) => setContact((c) => ({ ...c, [k]: v }))]));

  const pickModel = (key) => { setModelKey(key); setTimeout(() => setStep(1), 180); };

  const submit = async () => {
    if (!contact.name.trim()) return toast.error("First name is required");
    if (contact.phone.replace(/\D/g, "").length !== 10) return toast.error("Enter a valid 10-digit phone number");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(contact.email.trim())) return toast.error("Enter a valid email address");
    if (!contact.address.trim()) return toast.error("Address is required so we can confirm we service your area");
    if (leadId) return;
    submissionId.current ||= window.crypto?.randomUUID?.() || `sub_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/intake/submit`, {
        service_type: "swing_door", screen_count: qty, screen_type: `swing_${modelKey}_${color}`,
        full_name: contact.name.trim(), phone: contact.phone.trim(), email: contact.email.trim(),
        address: contact.address.trim(), zip_code: contact.zip.trim(),
        funnel_version: FUNNEL, funnel_session_id: sessionId, source: "screenzz.com", ...getAttribution(), submission_id: submissionId.current,
        ...utm(), referrer: document.referrer || null, landing_page: "/swinging-door-quote",
        estimate_data: { model_key: modelKey, color, upgrade_key: upgradeKey, qty },
      });
      const saved = data.estimate_data;
      if (saved?.line_items) setServerQuote({ items: saved.line_items, subtotal: saved.subtotal, tax: saved.tax, total: saved.total, discount: saved.discount, discountName: saved.discount_name, qty });
      setLeadId(data.lead_id);
      track("quote_revealed", { step: 99, metadata: { lead_id: data.lead_id, quote_value: quote.total } });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => (STEPS[step] === "contact" ? submit() : setStep(step + 1));

  if (leadId) return <QuoteReveal quote={serverQuote || quote} model={model} colorLabel={colorLabel} upgradeName={upgrade ? upgrade.name.replace(" (Swinging Door)", "") : "Standard Screen"} name={contact.name} leadId={leadId} />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-50 to-white pb-32" data-testid="swing-door-funnel">
      <header className="max-w-2xl mx-auto px-4 pt-5 pb-3 flex flex-col items-center gap-1.5">
        <img src="https://customer-assets.emergentagent.com/job_tech-dispatch-31/artifacts/a6ta9f48_SCREENZZ-LOGO-RGB_TextOnly.png" alt="ScreenzZ" className="h-10 sm:h-12 w-auto" />
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.18em] text-[#207024] font-semibold">Swinging Screen Doors</div>
      </header>
      {step > 0 && (
        <div className="max-w-2xl mx-auto px-4 mb-4">
          <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <div className="h-full bg-[#207024] transition-[width] duration-300" style={{ width: `${(step / (STEPS.length - 1)) * 100}%` }} />
          </div>
          <div className="text-[11px] text-zinc-500 mt-1.5 flex justify-between">
            <button onClick={() => setStep(step - 1)} className="text-[#207024] font-semibold flex items-center gap-1" data-testid="swing-back-btn"><ChevronLeft className="w-3 h-3" /> Back</button>
            <span>Step {step + 1} of {STEPS.length}</span>
          </div>
        </div>
      )}
      <main className="max-w-2xl mx-auto px-4">
        {!catalog ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#207024]" /></div>
        ) : (
          <>
            {STEPS[step] === "model" && <StepModel models={catalog.models} modelKey={modelKey} onPick={pickModel} />}
            {STEPS[step] === "color" && <StepColor colors={catalog.colors} color={color} setColor={setColor} />}
            {STEPS[step] === "upgrade" && <StepUpgrade upgrades={catalog.upgrades} upgradeKey={upgradeKey} setUpgradeKey={setUpgradeKey} />}
            {STEPS[step] === "qty" && <StepQty qty={qty} setQty={setQty} />}
            {STEPS[step] === "contact" && <StepContact c={contact} set={setters} />}
          </>
        )}
      </main>
      {step > 0 && (
        <footer className="fixed bottom-0 inset-x-0 bg-white border-t-2 border-zinc-200 shadow-lg">
          <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0" data-testid="swing-order-summary">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Your door</div>
              <div className="text-sm text-zinc-800 truncate"><span className="font-semibold text-[#207024]">{qty}×</span> {model?.name} · {colorLabel}{upgrade ? ` · ${upgrade.name.replace(" Upgrade (Swinging Door)", "")}` : ""}</div>
              <div className="text-sm font-bold text-zinc-900" data-testid="swing-running-total">{fmt(quote.subtotal)} <span className="text-[11px] font-normal text-zinc-500">+ tax</span></div>
            </div>
            <button onClick={next} disabled={submitting} data-testid="swing-continue-btn" className="h-12 px-5 rounded-lg bg-[#207024] hover:bg-[#1a5c1d] text-white font-bold flex items-center gap-2 disabled:opacity-60 shrink-0 transition-colors">
              {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : STEPS[step] === "contact" ? <>See My Quote <Sparkles className="w-4 h-4" /></> : <>Continue <ChevronRight className="w-4 h-4" /></>}
            </button>
          </div>
        </footer>
      )}
    </div>
  );
}
